import React, { useEffect } from 'react';
import { Link } from 'react-router-dom';
import { Helmet } from 'react-helmet-async';
import { motion } from 'framer-motion';
import { ExternalLink, Download, Check, BookOpen, Quote, ArrowRight, Microscope } from 'lucide-react';
import Header from '../components/layout/Header';
import Footer from '../components/layout/Footer';
import SEOHead from '../components/seo/SEOHead';
import { clinicalStudies } from '../data/clinicalStudies';
import ShareButtons from '../components/ui/ShareButtons';
import { useMotion } from '../context/MotionContext';
import { trackPageView } from '../utils/analytics';

const StudyCard = ({ study, index, reduced }) => (
  <motion.article
    initial={reduced ? {} : { opacity: 0, y: 28 }}
    whileInView={{ opacity: 1, y: 0 }}
    viewport={{ once: true }}
    transition={{ delay: index * 0.06 }}
    className="hud-card corner-brackets rounded-lg p-6 md:p-8"
    data-testid={`study-card-${study.id}`}
  >
    <div className="flex flex-wrap items-center gap-3 mb-4">
      <span className="inline-flex items-center gap-1.5 text-[#00FFD1] text-xs font-mono uppercase tracking-wider bg-[#00FFD1]/10 border border-[#00FFD1]/30 px-3 py-1 rounded-full">
        <BookOpen size={12} /> {study.journal}
      </span>
      <span className="text-white/50 text-xs font-mono">{study.year}</span>
      <span className="text-[#F08A24] text-xs font-mono uppercase tracking-wider bg-[#F08A24]/10 border border-[#F08A24]/30 px-3 py-1 rounded-full">
        {study.topic}
      </span>
    </div>

    <h2 className="text-xl md:text-2xl font-bold text-white leading-snug mb-2">{study.title}</h2>
    <p className="text-white/55 text-sm italic mb-5">{study.authors} · <span className="not-italic">{study.journal}</span>, {study.year}</p>

    <p className="text-white/75 leading-relaxed mb-5">{study.summary}</p>

    <div className="mb-5">
      <p className="terminal-label text-[#00FFD1] mb-3">// Key findings</p>
      <ul className="space-y-2">
        {study.findings.map((f, i) => (
          <li key={i} className="flex items-start gap-2 text-white/70 text-sm">
            <Check size={16} className="text-[#00FFD1] mt-0.5 shrink-0" />
            <span>{f}</span>
          </li>
        ))}
      </ul>
    </div>

    <div className="flex items-start gap-3 bg-[#00FFD1]/[0.06] border-l-2 border-[#00FFD1] rounded-r-md p-4 mb-5">
      <Quote size={18} className="text-[#00FFD1] shrink-0 mt-0.5" />
      <p className="text-white/80 text-sm leading-relaxed"><span className="text-[#00FFD1] font-semibold">Why it matters for XION: </span>{study.xionRelevance}</p>
    </div>

    <div className="flex flex-wrap gap-2 mb-6">
      {study.tags.map((t) => (
        <span key={t} className="text-white/55 text-xs font-mono bg-white/5 border border-white/10 px-2.5 py-1 rounded">#{t}</span>
      ))}
    </div>

    <div className="flex flex-wrap gap-3">
      <a
        href={study.doiUrl}
        target="_blank"
        rel="noopener noreferrer"
        className="btn-primary"
        data-testid={`study-doi-${study.id}`}
      >
        Read full study <ExternalLink size={15} />
      </a>
      <a
        href={study.pdf}
        target="_blank"
        rel="noopener noreferrer"
        className="btn-secondary"
        data-testid={`study-pdf-${study.id}`}
      >
        <Download size={15} /> PDF
      </a>
      {study.relatedLink && (
        <Link
          to={study.relatedLink.to}
          className="inline-flex items-center gap-1.5 text-[#00FFD1] text-sm font-mono px-3 py-2 hover:underline"
          data-testid={`study-related-${study.id}`}
        >
          {study.relatedLink.label} <ArrowRight size={14} />
        </Link>
      )}
    </div>
  </motion.article>
);

const ClinicalEvidencePage = () => {
  const { prefersReducedMotion } = useMotion();

  useEffect(() => {
    trackPageView('ClinicalEvidence');
  }, []);

  return (
    <>
      <SEOHead
        title="Clinical Evidence - XION Medical Philippines"
        description="Peer-reviewed studies validating XION technology: world-first 3D laryngostroboscopy, PIET image enhancement vs NBI in FEES, and 3D video-endoscopic surgical anatomy. Published in The Laryngoscope and European Archives of Oto-Rhino-Laryngology."
        path="/clinical-evidence"
        image="/products/transparent/endostrobe-spectar-system-web.png"
      />
      <Helmet>
        <script type="application/ld+json">
          {JSON.stringify({
            '@context': 'https://schema.org',
            '@type': 'ItemList',
            name: 'XION Clinical Evidence',
            itemListElement: clinicalStudies.map((s, i) => ({
              '@type': 'ListItem',
              position: i + 1,
              item: {
                '@type': 'ScholarlyArticle',
                headline: s.title,
                author: s.authors,
                isPartOf: { '@type': 'Periodical', name: s.journal },
                datePublished: String(s.year),
                identifier: s.doi,
                sameAs: s.doiUrl,
              },
            })),
          })}
        </script>
      </Helmet>
      <Header />
      <main className="pt-32 min-h-screen bg-hud" data-testid="clinical-evidence-page">
        <div className="max-w-5xl mx-auto px-6 py-16">
          <motion.div
            initial={prefersReducedMotion ? {} : { opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            className="text-center mb-16"
          >
            <p className="terminal-label text-[#00FFD1] mb-4 inline-flex items-center gap-2 justify-center">
              <Microscope size={14} /> // Peer-Reviewed Science
            </p>
            <h1 className="text-4xl md:text-6xl font-bold text-white mb-6">Clinical Evidence</h1>
            <p className="text-lg md:text-xl text-white/70 max-w-3xl mx-auto leading-relaxed">
              XION technology isn&apos;t just engineered in Germany &mdash; it&apos;s validated in the
              peer-reviewed literature. These independent studies, published in leading ENT and
              phoniatrics journals, examine the 3D laryngostroboscopy and PIET image enhancement that
              power XION systems.
            </p>
            <div className="flex justify-center mt-6">
              <ShareButtons title="XION Medical Philippines — Clinical Evidence" label="Share" testId="evidence-share" />
            </div>
          </motion.div>

          <div className="space-y-8">
            {clinicalStudies.map((study, i) => (
              <StudyCard key={study.id} study={study} index={i} reduced={prefersReducedMotion} />
            ))}
          </div>

          <motion.div
            initial={prefersReducedMotion ? {} : { opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            className="mt-16 bg-gradient-to-br from-[#00FFD1]/10 to-transparent border-2 border-[#00FFD1]/50 rounded-lg p-8 text-center"
          >
            <h2 className="text-2xl font-bold text-white mb-3">See the evidence in your own clinic</h2>
            <p className="text-white/70 mb-6 max-w-2xl mx-auto">
              Request a hands-on demonstration of XION 3D stroboscopy and PIET with FMSInc., the
              exclusive XION distributor in the Philippines.
            </p>
            <Link to="/contact-ph" state={{ productName: 'a XION clinical demonstration' }} className="btn-primary inline-flex" data-testid="evidence-cta">
              Request a Demonstration <ArrowRight size={16} />
            </Link>
          </motion.div>

          <p className="text-white/35 text-xs text-center mt-10 max-w-2xl mx-auto">
            Citations are provided for reference and link to the publisher of record. Studies are the
            work of their respective authors and journals.
          </p>
        </div>
      </main>
      <Footer />
    </>
  );
};

export default ClinicalEvidencePage;
